import {observer} from "mobx-react";
import {useContext} from "react";
import {LinearProgress, Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import EditIcon from "@mui/icons-material/Edit";
import {Frame} from "../../common/Frame";
import {LessonEditorStoreContext} from "../../../stores/LessonEditorStore";
import {lessons} from "../../../proto/compiled";
import {StyleSelector} from "./StyleSelector";
import {RichTextEditor} from "./RichTextEditor";
import "react-quill-new/dist/quill.snow.css";
import {SanitizedHtml} from "../../common/SanitizedHtml";
import {textBlockStyleColor, textBlockStyleProps} from "../TextBlockStyles";

interface TextBlockEditorProps {
  block: lessons.IContentBlock;
  isEditing: boolean;
}

export const TextBlockEditor = observer(({block, isEditing}: TextBlockEditorProps) => {
  const store = useContext(LessonEditorStoreContext);
  const theme = useTheme();

  if (!store) return null;

  const styleProps = textBlockStyleProps(block.textStyle, theme);
  const hasStyle = !!textBlockStyleColor(block.textStyle, theme);

  if (isEditing) {
    const editor = (
      <RichTextEditor
        value={block.richText ?? ""}
        onChange={(value) => {
          block.richText = value;
        }}
        onUpload={(file) => store.uploadAsset(file)}
      />
    );

    return (
      <Frame noTransition direction="column" gap={2} padding={1}>
        <StyleSelector
          value={block.textStyle ?? lessons.TextBlockStyle.TEXT_STYLE_NORMAL}
          onChange={(style) => {
            block.textStyle = style;
          }}
        />
        {hasStyle ? (
          <Frame
            noTransition
            padding={1.5}
            radius={0.5}
            style={styleProps}
          >
            {editor}
          </Frame>
        ) : editor}
        {store.assetUploadProgress !== undefined && (
          <LinearProgress variant="determinate" value={store.assetUploadProgress * 100}/>
        )}
      </Frame>
    );
  }

  const hasContent = block.richText && block.richText.trim().length > 0 && block.richText !== "<p><br></p>";

  return (
    <Frame
      noTransition
      padding={2}
      style={styleProps}
      radius={0.5}
    >
      {hasContent ? (
        <div
          className="ql-editor"
          style={{
            padding: 0,
            color: theme.palette.text.primary,
          }}
        >
          <SanitizedHtml html={block.richText ?? ''}/>
        </div>
      ) : (
        <Frame row alignItems="center" gap={1}>
          <EditIcon fontSize="small" color="disabled"/>
          <Typography variant="body2" color="textSecondary" fontStyle="italic">
            Click to add text content...
          </Typography>
        </Frame>
      )}
    </Frame>
  );
});
